import { useState } from "react";
import { FileText, Loader2, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { StatusBadge, VisibilityBadge, PublicationBadge, Stars } from "../common/Badges";
import { CATEGORY, STATUS, fmtDate } from "../../lib/labels";
import { formatApiError } from "../../lib/api";
import { openAttachment, fmtSize } from "../../lib/files";

const Bubble = ({ r, companyName }) => {
  const staff = r.author_type === "entreprise";
  const ask = staff && r.kind === "demande_precision";
  return (
    <div data-testid="thread-message" className={`rounded-xl p-4 ${ask ? "ml-4 border border-violet-200 bg-violet-50 sm:ml-8" : staff ? "ml-4 border border-blue-100 bg-blue-50 sm:ml-8" : "mr-4 bg-slate-100 sm:mr-8"}`}>
      <p className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-slate-500">
        {ask && <MessageCircleQuestion className="h-3.5 w-3.5 text-violet-700" />}
        {staff ? companyName : "Vous"}{ask ? " · Demande de précision" : staff ? (r.public ? " · Réponse publique" : " · Réponse privée") : ""} · {fmtDate(r.created_at)}
      </p>
      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{r.message}</p>
    </div>
  );
};

const ReplyBox = ({ onReply, highlight }) => {
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (msg.trim().length < 2) { toast.error("Votre message est trop court"); return; }
    setSending(true);
    try { await onReply(msg); setMsg(""); toast.success("Message envoyé à l'organisation"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };
  return (
    <div className={`mt-6 space-y-2 rounded-xl ${highlight ? "border border-violet-200 bg-violet-50/50 p-4" : ""}`}>
      {highlight && <p className="text-sm font-medium text-violet-800">L'organisation attend une précision de votre part.</p>}
      <Textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Votre réponse ou précision…" data-testid="thread-reply-input" />
      <Button onClick={send} disabled={sending} className="rounded-full" data-testid="thread-reply-submit">{sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Envoyer</Button>
    </div>
  );
};

const StatusTimeline = ({ items }) => (
  <div className="mt-6 border-t pt-4" data-testid="thread-status-history">
    <p className="eyebrow mb-2">Historique des statuts</p>
    <ul className="space-y-1 text-xs text-slate-600">
      {items.map((h, i) => <li key={i}>{fmtDate(h.at)} · {STATUS[h.to]?.label || h.to} <span className="text-slate-400">({h.by})</span></li>)}
    </ul>
  </div>
);

export default function FeedbackThread({ fb, onReply, attachmentBase }) {
  return (
    <div className="card-surface rise p-5 sm:p-6" data-testid="feedback-thread">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm font-semibold" data-testid="thread-code">{fb.tracking_code}</span>
        <StatusBadge value={fb.status} /><VisibilityBadge value={fb.visibility} />
        {fb.publication && <PublicationBadge value={fb.publication} />}
        <Stars value={fb.rating} />
      </div>
      <p className="mt-2 text-sm text-slate-500">{fb.company_name}{fb.agency_name ? ` · ${fb.agency_name}` : ""}{fb.service_name ? ` · ${fb.service_name}` : ""}</p>
      <p className="mt-1 text-xs text-slate-400">{fb.category ? CATEGORY[fb.category] : ""}{fb.experience_date ? ` · Expérience du ${fmtDate(fb.experience_date, false)}` : ""}</p>
      <div className="mt-5 space-y-3">
        <div className="rounded-xl bg-slate-100 p-4">
          <p className="text-xs font-semibold text-slate-500">Votre message · {fmtDate(fb.created_at)}</p>
          {fb.title && <p className="mt-1 font-semibold text-slate-900">{fb.title}</p>}
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{fb.message}</p>
          {fb.attachments?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {fb.attachments.map((a) => attachmentBase ? (
                <button key={a.id} onClick={() => openAttachment(`${attachmentBase}/${a.id}`)} data-testid={`thread-attachment-${a.id}`}
                  className="flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1 text-xs text-blue-700 hover:underline"><FileText className="h-3.5 w-3.5" />{a.filename} ({fmtSize(a.size)})</button>
              ) : <span key={a.id} className="flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1 text-xs text-slate-600"><FileText className="h-3.5 w-3.5" />{a.filename}</span>)}
            </div>
          )}
        </div>
        {fb.responses.map((r) => <Bubble key={r.id} r={r} companyName={fb.company_name} />)}
        {!fb.responses.length && <p className="text-sm text-slate-500">L'organisation n'a pas encore répondu.</p>}
      </div>
      {fb.conversation_open ? <ReplyBox onReply={onReply} highlight={fb.status === "attente_usager"} /> : <p className="mt-6 text-sm text-slate-500" data-testid="thread-closed">Cette conversation a été fermée par l'organisation.</p>}
      {fb.status_history?.length > 0 && <StatusTimeline items={fb.status_history} />}
    </div>
  );
}
